import { useCallback, useRef, useEffect, useReducer } from 'react';
import { platforms } from '../config/platforms';
import { usePlatforms } from '../contexts/PlatformContext';
import { openIntent } from '../utils/openIntent';
import { getAuthHeaders } from '../lib/authFetch';

type PlatformKey = keyof typeof platforms;
type Status = 'pending' | 'success' | 'error' | 'popup-blocked' | 'manual' | 'already-posted';

export interface PlatformStatus {
  status: Status;
  error?: string;
  code?: string;
  resetAt?: number; // epoch ms for rate limit reset
}

// Reducer logic for state management
type CrosspostState = {
  statuses: Record<PlatformKey, PlatformStatus>;
  posting: boolean;
  publishedPostId: string | null;
};

// API response types
interface CrosspostApiResponse {
  ok: boolean;
  code?: string;
  error?: string;
  resetAt?: string;
  message?: string;
}

type CrosspostAction =
  | { type: 'START_POSTING'; payload: { content: string; userId: string; } }
  | { type: 'ABORT' }
  | { type: 'SET_INTENT_STATUSES'; payload: { statuses: Partial<Record<PlatformKey, PlatformStatus>> } }
  | { type: 'SET_OAUTH_RESULTS'; payload: { results: Record<string, CrosspostApiResponse>; platforms: PlatformKey[]; publishedPostId?: string | null } }
  | { type: 'SET_ERROR'; payload: { error: string; platforms: PlatformKey[] } }
  | { type: 'SET_ALL_OAUTH_TO_ALREADY_POSTED'; payload: { error: string, platforms: PlatformKey[] } }
  | { type: 'FINISH_POSTING' }
  | { type: 'RETRY_INTENT'; payload: { key: PlatformKey, status: PlatformStatus } };


const makeInitialStatus = (): Record<PlatformKey, PlatformStatus> => {
  return (Object.keys(platforms) as PlatformKey[]).reduce((acc, key) => {
    acc[key] = { status: 'pending' };
    return acc;
  }, {} as Record<PlatformKey, PlatformStatus>);
};

const initialState: CrosspostState = {
  statuses: makeInitialStatus(),
  posting: false,
  publishedPostId: null,
};

function crosspostReducer(state: CrosspostState, action: CrosspostAction): CrosspostState {
  switch (action.type) {
    case 'START_POSTING':
      return { ...initialState, posting: true };
    case 'ABORT':
        return { ...state, posting: false };
    case 'SET_INTENT_STATUSES':
      return { ...state, statuses: { ...state.statuses, ...action.payload.statuses } as Record<PlatformKey, PlatformStatus> };
    case 'SET_OAUTH_RESULTS': {
      const newStatuses = { ...state.statuses };
      action.payload.platforms.forEach(key => {
        const result = action.payload.results[key];
        if (result) {
          if (result.code === 'ALREADY_POSTED') {
            newStatuses[key] = { status: 'already-posted', error: result.error || 'Already posted' };
          } else if (result.code === 'RATE_LIMIT') {
            newStatuses[key] = { status: 'error', error: result.error || 'Rate limited', code: 'RATE_LIMIT', resetAt: result.resetAt ? new Date(result.resetAt).getTime() : undefined };
          } else if (result.code === 'TOKEN_EXPIRED') {
            newStatuses[key] = { status: 'error', error: result.error || 'Token expired', code: 'TOKEN_EXPIRED' };
          } else if (result.code === 'DUPLICATE_POST') {
            newStatuses[key] = { status: 'already-posted', error: result.error || 'Duplicate post' };
          } else if (result.ok) {
            newStatuses[key] = { status: 'success' };
          } else if (result.error) {
            newStatuses[key] = { status: 'error', error: result.error, code: result.code };
          } else if (result.message) {
            newStatuses[key] = { status: 'error', error: result.message, code: result.code };
          } else {
            newStatuses[key] = { status: 'error', error: 'An unknown server error occurred.', code: result.code };
          }
        } else {
          newStatuses[key] = { status: 'error', error: 'No response from server' };
        }
      });
      return { ...state, statuses: newStatuses, publishedPostId: action.payload.publishedPostId ?? state.publishedPostId };
    }
    case 'SET_ALL_OAUTH_TO_ALREADY_POSTED': {
      const newStatuses = { ...state.statuses };
      action.payload.platforms.forEach(key => {
        newStatuses[key] = { status: 'already-posted', error: action.payload.error };
      });
      return { ...state, statuses: newStatuses, posting: false };
    }
    case 'SET_ERROR': {
      const newStatuses = { ...state.statuses };
      action.payload.platforms.forEach(key => {
        newStatuses[key] = { status: 'error', error: action.payload.error || 'Failed to post' };
      });
      return { ...state, statuses: newStatuses };
    }
    case 'FINISH_POSTING':
      return { ...state, posting: false };
    case 'RETRY_INTENT':
        return { ...state, statuses: { ...state.statuses, [action.payload.key]: action.payload.status }};
    default:
      return state;
  }
}


type Attachment = { type: 'image'; url: string; alt?: string };

export const useCrosspost = () => {
  const { enabledPlatforms, platformConnections, postingMethods } = usePlatforms();
  const [state, dispatch] = useReducer(crosspostReducer, initialState);
  const contentRef = useRef('');
  const userIdRef = useRef('');
  const aborted = useRef(false);
  const connectionsRef = useRef(platformConnections);

  useEffect(() => {
    connectionsRef.current = platformConnections;
  }, [platformConnections]);

  const attachmentsRef = useRef<Attachment[]>([]);
  const replyToPostIdRef = useRef<string | undefined>(undefined);
  const quotePostIdRef = useRef<string | undefined>(undefined);

  const crosspost = useCallback(async (platformsToPost?: PlatformKey[], silent?: boolean) => {
    const content = contentRef.current;
    const userId = userIdRef.current;
    
    const allSelectedPlatforms = platformsToPost || (Object.keys(platforms) as PlatformKey[]).filter(key => enabledPlatforms[key]);
    const activeSelectedPlatforms = allSelectedPlatforms;

    if (allSelectedPlatforms.length === 0) {
      if (!platformsToPost && !silent) dispatch({ type: 'FINISH_POSTING' });
      return;
    }

    // Separate platforms by chosen method
    const oauthPlatforms = activeSelectedPlatforms.filter(key => connectionsRef.current[key] && postingMethods[key] === 'oauth');
    const intentPlatforms = activeSelectedPlatforms.filter(key => !connectionsRef.current[key] || postingMethods[key] === 'intent');

    // Set all selected platforms to pending
    const statusUpdates: Partial<Record<PlatformKey, PlatformStatus>> = {};
    allSelectedPlatforms.forEach(key => {
      statusUpdates[key] = { status: 'pending' };
    });
    dispatch({ type: 'SET_INTENT_STATUSES', payload: { statuses: statusUpdates } });

    // If no active platforms remain, finish early.
    if (activeSelectedPlatforms.length === 0) {
      if (!silent) dispatch({ type: 'FINISH_POSTING' });
      return;
    }

    // Defer intent opening to avoid blocking React's commit phase
    intentPlatforms.forEach(key => {
        setTimeout(() => {
            if (aborted.current) return;
            const url = platforms[key].intentUrl + encodeURIComponent(content);
            const winOk: PlatformStatus = openIntent(url)
              ? { status: 'manual' }
              : { status: 'popup-blocked', error: 'Popup was blocked' };
            dispatch({ type: 'RETRY_INTENT', payload: { key, status: winOk } });
        }, 0)
    });

    // Handle OAuth platforms by calling the backend
    if (oauthPlatforms.length > 0) {
      try {
        const base = process.env.REACT_APP_API_URL;
        const isLocalHost = /^(localhost|127\.0\.0\.1|192\.168\.\d+\.\d+)$/.test(window.location.hostname);
        const url = isLocalHost
          ? `/api/crosspost`
          : (base ? `${base.replace(/\/$/, '')}/api/crosspost` : `/api/crosspost`);
        console.log('[useCrosspost]', { 
          hostname: window.location.hostname, 
          base, 
          isLocalHost, 
          url 
        });
        console.debug('[useCrosspost] POST crosspost', { url, oauthPlatforms });
        const response = await fetch(url, {
          method: 'POST',
          headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({
            userId,
            content,
            platforms: oauthPlatforms,
            attachments: attachmentsRef.current,
            skipDedupe: true,
            replyToPostId: replyToPostIdRef.current,
            quotePostId: quotePostIdRef.current
          })
        });
        if (aborted.current) return;
        const contentType = response.headers.get('content-type') || '';
        const result = contentType.includes('application/json') ? await response.json() : { error: await response.text() };
        if (aborted.current) return;
        
        if (!response.ok) {
          if (response.status === 409) {
            dispatch({ type: 'SET_ALL_OAUTH_TO_ALREADY_POSTED', payload: { error: result.error, platforms: oauthPlatforms } });
          } else {
            const errorMessage = result.error || `Request failed with status ${response.status}`;
            dispatch({ type: 'SET_ERROR', payload: { error: errorMessage, platforms: oauthPlatforms } });
          }
        } else {
          const publishedPostId = typeof result?.publishedPostId === 'string' ? result.publishedPostId : null;
          dispatch({ type: 'SET_OAUTH_RESULTS', payload: { results: result, platforms: oauthPlatforms, publishedPostId } });
        }

      } catch (e: unknown) {
        if (aborted.current) return;
        const errorMessage = e instanceof Error ? e.message : String(e);
        dispatch({ type: 'SET_ERROR', payload: { error: errorMessage, platforms: oauthPlatforms } });
      }
    }
    
    if (!platformsToPost && !silent) {
      dispatch({ type: 'FINISH_POSTING' });
    }
  }, [enabledPlatforms, postingMethods]);

  const startCrossposting = useCallback((
    content: string,
    userId: string,
    attachments?: Attachment[],
    replyToPostId?: string,
    quotePostId?: string
  ) => {
    aborted.current = false;
    contentRef.current = content;
    userIdRef.current = userId;
    attachmentsRef.current = attachments || [];
    replyToPostIdRef.current = replyToPostId;
    quotePostIdRef.current = quotePostId;
    dispatch({ type: 'START_POSTING', payload: { content, userId } });
    crosspost();
  }, [crosspost]);

  const abort = useCallback(() => {
    aborted.current = true;
    dispatch({ type: 'ABORT' });
  }, []);

  const handleRetry = (key: PlatformKey) => {
    if (connectionsRef.current[key] && postingMethods[key] === 'oauth') {
      crosspost([key], true);
    } else {
      const url = platforms[key].intentUrl + encodeURIComponent(contentRef.current);
      // do NOT block commit
      setTimeout(() => {
        const winOk: PlatformStatus = openIntent(url)
          ? { status: 'manual' }
          : { status: 'popup-blocked', error: 'Popup was blocked' };
        dispatch({ type: 'RETRY_INTENT', payload: { key, status: winOk } });
      }, 0);
    }
  };

  return { statuses: state.statuses, posting: state.posting, publishedPostId: state.publishedPostId, handleRetry, startCrossposting, abort, enabledPlatforms, platformConnections, postingMethods };
}; 
